import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ampli } from '@/ampli';
import { ConnectedWallet } from '@privy-io/react-auth';
import { ConnectedStandardSolanaWallet } from '@privy-io/react-auth/solana';
import { useCallback, useState } from 'react';
import { useMedia } from 'react-use';

import { TextSkeleton } from '@/components/Skeleton';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import { buttons } from '@/styles/buttons';
import { popover as popoverStyles } from '@/styles/popover';
import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { WalletAvatar } from '@/ui/Avatars/WalletAvatar';
import { ArrowsSwitchIcon } from '@/ui/Icons/ArrowsSwitchIcon';
import { DisconnectIcon } from '@/ui/Icons/DisconnectIcon';
import { XIcon } from '@/ui/Icons/XIcon';
import { BottomPanelModal } from '@/ui/Modal/BottomPanelModal';

import { classNames } from '@/utils/classnames';
import { ChainAddress } from '@/utils/models';

import { PasteOrConnectWalletModal } from '../PasteOrConnectWalletModal';

export const ConnectedWalletLine = ({
  isLoading,
  label,
  wallet,
  pastedReceivingWalletAddress,
  side,
  resetPastedReceivingWalletAddress,
  chainId,
}: {
  isLoading: boolean;
  label: string;
  wallet: ConnectedWallet | ConnectedStandardSolanaWallet | undefined;
  pastedReceivingWalletAddress?: ChainAddress | undefined;
  side: 'buy' | 'sell';
  resetPastedReceivingWalletAddress?: () => void;
  chainId?: number | undefined;
}) => {
  const [pasteOrConnectWalletModalOpen, setPasteOrConnectWalletModalOpen] = useState(false);

  return (
    <div className="relative flex justify-between items-center py-2.5 px-6">
      <span
        className={fontStyles({
          variant: 'BODY_MD',
          class: ['items-center', 'flex', 'h-6', 'text-token-gray-800'],
        })}
      >
        {label}
      </span>
      {isLoading ? (
        <TextSkeleton width={100} height={14} />
      ) : (
        <>
          <WalletDropdown
            wallet={wallet}
            pastedReceivingWalletAddress={pastedReceivingWalletAddress}
            side={side}
            openPasteOrConnectWalletModal={() => setPasteOrConnectWalletModalOpen(true)}
            resetPastedReceivingWalletAddress={resetPastedReceivingWalletAddress}
          />
          <PasteOrConnectWalletModal
            isOpen={pasteOrConnectWalletModalOpen}
            onOpenChange={setPasteOrConnectWalletModalOpen}
            onClose={() => setPasteOrConnectWalletModalOpen(false)}
            chainId={chainId}
            popoverClass="border-token-gray-300 bg-special-background-modal absolute h-fit rounded-[1.125rem] z-100 shadow-25 bottom-11 left-auto right-6 w-[80%]"
          />
        </>
      )}
    </div>
  );
};

const shortAddress = (address: string) => `${address.slice(0, 4)}…${address.slice(-4)}`;

const WalletDropdown = ({
  wallet,
  pastedReceivingWalletAddress,
  side,
  openPasteOrConnectWalletModal,
  resetPastedReceivingWalletAddress,
}: {
  wallet: ConnectedWallet | ConnectedStandardSolanaWallet | undefined;
  pastedReceivingWalletAddress: ChainAddress | undefined;
  side: 'buy' | 'sell';
  openPasteOrConnectWalletModal?: () => void;
  resetPastedReceivingWalletAddress?: () => void;
}) => {
  const { base: popoverClass, icon: popoverIconClass, item: popoverItemClass } = popoverStyles();

  const [open, setOpen] = useState(false);
  const isMobile = useMedia(breakpoints.mobile, false);

  const { connect, disconnectEthereum, disconnectSolana } = useMatchaWallets();

  const onSwitch = () => {
    // If buy side, we want to allow the user to paste or connect a wallet
    if (side === 'buy' && openPasteOrConnectWalletModal) {
      setOpen(false);
      openPasteOrConnectWalletModal();
      return;
    }

    // If sell side, we want to connect a new wallet with Privy
    connect();
    setTimeout(() => {
      // focus the modal once the modal is animated in
      (
        document.querySelector(
          '#privy-modal-content button.login-method-button:first-child',
        ) as HTMLButtonElement
      )?.focus();
    }, 200);
    // we need to close the dropdown to allow the privy modal becoming scrollable
    setOpen(false);
  };

  const onDisconnect = useCallback(() => {
    if (!wallet) return;
    if ('standardWallet' in wallet) {
      void disconnectSolana();
    } else {
      void disconnectEthereum();
    }
    ampli.walletDisconnectClicked({
      source: 'trade_module',
      ui_location: 'cross_chain_trade_module',
    });
    setOpen(false);
  }, [wallet, disconnectEthereum, disconnectSolana]);

  const onRemove = () => {
    resetPastedReceivingWalletAddress?.();
    setOpen(false);
  };

  const buttonContent = (
    <>
      {wallet && <WalletAvatar wallet={wallet} size={24} />}
      <span className="text-token-gray-1600 ml-2">
        {wallet
          ? shortAddress(wallet.address)
          : pastedReceivingWalletAddress
            ? shortAddress(pastedReceivingWalletAddress)
            : ''}
      </span>
    </>
  );

  return isMobile ? (
    <>
      <button
        className={buttons({
          class: ['cursor-pointer', '-mr-3', '-mt-1.5', '-mb-1.5'],
          color: 'transparent',
          iconOnly: true,
          size: 'small',
          shape: 'rounded',
        })}
        aria-label="Wallet actions"
        onClick={() => setOpen(true)}
      >
        {buttonContent}
      </button>

      <BottomPanelModal
        variant="LARGE"
        isOpen={open}
        onOpenChange={setOpen}
        onClose={() => setOpen(false)}
        allowGestures={isMobile}
      >
        <Dialog.Close
          className={buttons({
            color: 'transparent',
            shape: 'rounded',
            iconOnly: true,
            class: 'absolute -mr-3 -my-2 top-6 left-[534px] hidden tablet:block pt-1',
          })}
          aria-label="close switch wallet dialog"
        >
          <XIcon aria-hidden className="size-4" />
        </Dialog.Close>

        <div className="p-4 pt-8">
          <h1 className={fontStyles({ variant: 'H5', class: ['mb-1', 'text-token-gray-1600'] })}>
            {side === 'buy' ? 'Receiving' : 'Sending'} Wallet
          </h1>

          <div className="mt-3 flex flex-col">
            <button
              className={fontStyles({
                variant: 'LABEL_LG',
                class: ['text-token-gray-1600', 'flex', 'gap-2', 'items-center', 'h-10', '-ml-2'],
              })}
              onClick={onSwitch}
            >
              <ArrowsSwitchIcon />
              Switch wallet
            </button>
            {wallet ? (
              <button
                className={fontStyles({
                  variant: 'LABEL_LG',
                  class: ['text-token-gray-1600', 'flex', 'gap-2', 'items-center', 'h-10', '-ml-2'],
                })}
                onClick={onDisconnect}
              >
                <DisconnectIcon />
                Disconnected
              </button>
            ) : (
              <button
                className={fontStyles({
                  variant: 'LABEL_LG',
                  class: ['text-token-gray-1600', 'flex', 'gap-2', 'items-center', 'h-10', '-ml-2'],
                })}
                onClick={onRemove}
              >
                <XIcon className="w-4 h-4" />
                Remove
              </button>
            )}
          </div>
        </div>
      </BottomPanelModal>
    </>
  ) : (
    <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenu.Trigger
        className={buttons({
          class: ['cursor-pointer', '-mr-3', '-mt-1.5', '-mb-1.5'],
          color: 'transparent',
          iconOnly: true,
          size: 'small',
          shape: 'rounded',
        })}
        aria-label="Wallet actions"
      >
        {buttonContent}
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          side="top"
          sideOffset={10}
          className={classNames(popoverClass(), 'min-w-auto')}
        >
          <DropdownMenu.Item key={'switch'} className={popoverItemClass()} onClick={onSwitch}>
            <ArrowsSwitchIcon className={popoverIconClass()} />
            Switch wallet
          </DropdownMenu.Item>

          {wallet ? (
            <DropdownMenu.Item
              key={'disconnect'}
              className={popoverItemClass()}
              onClick={onDisconnect}
            >
              <DisconnectIcon className={popoverIconClass()} />
              Disconnect
            </DropdownMenu.Item>
          ) : (
            <DropdownMenu.Item key={'remove'} className={popoverItemClass()} onClick={onRemove}>
              <XIcon className={popoverIconClass()} />
              Remove
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
